import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { handleError, handleSuccess } from '../utils';
import './MyRides.css';

function MyRides() {
    const [rides, setRides] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deletingId, setDeletingId] = useState(null);
    const navigate = useNavigate();

    // ✅ Fetch user's rides on mount
    useEffect(() => {
        const fetchMyRides = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/rides/my-rides`, {
                    headers: {
                        'Authorization': token
                    }
                });

                const result = await response.json();

                if (result.success) {
                    setRides(result.rides);
                } else {
                    handleError(result.message || 'Failed to fetch rides');
                }
            } catch (error) {
                console.error('Error fetching rides:', error);
                handleError('Error loading rides');
            } finally {
                setLoading(false);
            }
        };

        fetchMyRides();
    }, []);

    // ✅ Delete a ride
    const handleDelete = async (rideId) => {
        if (!window.confirm('Are you sure you want to delete this ride?')) {
            return;
        }

        setDeletingId(rideId);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/rides/${rideId}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Authorization': token
                    }
                }
            );

            const result = await response.json();

            if (result.success) {
                setRides(rides.filter(ride => ride._id !== rideId));
                handleSuccess('Ride deleted successfully');
            } else {
                handleError(result.message || 'Failed to delete ride');
            }
        } catch (error) {
            console.error('Error deleting ride:', error);
            handleError('Error deleting ride');
        } finally {
            setDeletingId(null);
        }
    };

    if (loading) {
        return (
            <div className="ui-page">
                <div className="ui-container">
                    <div className="ui-card">
                        <div className="ui-card-body">
                            <p className="ui-subtitle" style={{ margin: 0 }}>Loading your rides…</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="ui-page">
            <div className="ui-container" style={{ maxWidth: 1040 }}>
                <div className="ui-header">
                    <div>
                        <h1 className="ui-title">My rides</h1>
                        <p className="ui-subtitle">
                            {rides.length} {rides.length === 1 ? 'ride' : 'rides'} posted
                        </p>
                    </div>
                    <div className="ui-actions">
                        <button className="ui-btn ui-btn-secondary" onClick={() => navigate('/profile')}>Profile</button>
                        <button className="ui-btn ui-btn-secondary" onClick={() => navigate('/home')}>Home</button>
                    </div>
                </div>

                {rides.length === 0 ? (
                    <div className="ui-card">
                        <div className="ui-card-body" style={{ textAlign: 'center', padding: '2.25rem 1.5rem' }}>
                            <div className="ui-card-title" style={{ marginBottom: '0.35rem' }}>No rides yet</div>
                            <p className="ui-subtitle" style={{ marginTop: 0 }}>
                                You haven’t posted any rides. Create one to help other NITK students.
                            </p>
                            <div style={{ marginTop: '1rem' }}>
                                <button className="ui-btn ui-btn-success" onClick={() => navigate('/home')}>
                                    Add a ride
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="ui-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1rem' }}>
                        {rides.map((ride) => (
                            <div key={ride._id} className="ui-card">
                                <div className="ui-card-body">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                                        <div style={{ fontWeight: 900, letterSpacing: '-0.02em' }}>
                                            {ride.from} → {ride.to}
                                        </div>
                                        {ride.femaleOnly && <span className="ui-badge ui-badge-warn">Female-only</span>}
                                    </div>

                                    <div className="ui-divider" style={{ margin: '0.9rem 0' }} />

                                    <div className="ui-kv">
                                        <div><strong>Date</strong><div>{ride.date}</div></div>
                                        <div><strong>Time</strong><div>{ride.time}</div></div>
                                    </div>

                                    <div className="ui-divider" style={{ margin: '0.9rem 0' }} />

                                    <div className="ui-subtitle" style={{ margin: 0, fontSize: '0.9rem' }}>
                                        Posted on{' '}
                                        {new Date(ride.createdAt).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </div>

                                    <div style={{ marginTop: '0.9rem' }}>
                                        <button
                                            className="ui-btn ui-btn-danger ui-btn-block"
                                            onClick={() => handleDelete(ride._id)}
                                            disabled={deletingId === ride._id}
                                        >
                                            {deletingId === ride._id ? 'Deleting…' : 'Delete'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <ToastContainer />
        </div>
    );
}

export default MyRides;
